import { useRef, useState } from "react";
import { Camera, ShieldCheck, Clock, IdentificationCard } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fileToResizedDataUrl } from "@/lib/image";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const LICENSE_CLASSES = ["GDL", "B2", "B", "D", "DA"];

export default function LicenseUploader({ uid, license, onDone }) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [licClass, setLicClass] = useState("GDL");
  const [expiry, setExpiry] = useState("");
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setPreview(await fileToResizedDataUrl(file));
    } catch {
      toast.error("Failed to read image");
    }
    e.target.value = "";
  };

  const upload = async () => {
    if (!preview) {
      toast.error("Choose a license photo first");
      return;
    }
    if (!expiry) {
      toast.error("License expiry date is required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/staff/${uid}/license`, { image: preview, license_class: licClass, expiry_date: expiry });
      toast.success("License uploaded — pending verification");
      setPreview(null);
      setExpiry("");
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="license-uploader">
      {license?.license_class ? (
        <div className="flex flex-wrap items-center gap-3 rounded-sm border border-border bg-background p-4" data-testid="license-current">
          <IdentificationCard size={24} className="text-primary" />
          <Badge variant="outline" className="rounded-sm border-primary/50 font-mono font-bold text-primary">{license.license_class}</Badge>
          {license.expiry_date && <span className="font-mono text-xs text-muted-foreground">Exp: {license.expiry_date}</span>}
          {license.verified ? (
            <Badge variant="outline" className="rounded-sm border-emerald-500/40 bg-emerald-500/10 font-bold uppercase tracking-wider text-emerald-400">
              <ShieldCheck size={12} className="mr-1" /> Verified
            </Badge>
          ) : (
            <Badge variant="outline" className="rounded-sm border-amber-500/40 bg-amber-500/10 font-bold uppercase tracking-wider text-amber-400">
              <Clock size={12} className="mr-1" /> Pending verification
            </Badge>
          )}
          {license.path && (
            <a href={fileUrl(license.path)} target="_blank" rel="noreferrer" data-testid="license-view-link"
              className="text-xs font-bold uppercase tracking-wider text-muted-foreground underline-offset-4 transition-colors hover:text-primary hover:underline">
              View document
            </a>
          )}
        </div>
      ) : (
        <p className="rounded-sm border border-dashed border-input p-4 text-sm text-muted-foreground" data-testid="license-empty">
          No driving license uploaded yet.
        </p>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_auto_auto]">
        <div
          data-testid="license-upload-zone"
          onClick={() => inputRef.current?.click()}
          className="flex cursor-pointer items-center justify-center gap-2 rounded-sm border-2 border-dashed border-input px-4 py-4 transition-colors hover:border-primary/50 hover:bg-primary/5"
        >
          <Camera size={18} className="text-muted-foreground" />
          <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            {preview ? "Photo selected" : "Upload license photo"}
          </span>
        </div>
        <Select value={licClass} onValueChange={setLicClass}>
          <SelectTrigger data-testid="license-class-select" className="rounded-sm border-input bg-background focus:ring-primary sm:w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-popover">
            {LICENSE_CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <input
          type="date"
          data-testid="license-expiry-input"
          value={expiry}
          onChange={(e) => setExpiry(e.target.value)}
          className="h-10 rounded-sm border border-input bg-background px-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <Button data-testid="license-upload-button" onClick={upload} disabled={busy || !preview}
          className="rounded-sm bg-primary font-bold uppercase tracking-wider text-primary-foreground hover:bg-amber-400 hover:text-black">
          {busy ? "Uploading..." : "Upload"}
        </Button>
      </div>
      {preview && (
        <img src={preview} alt="license preview" className="max-h-48 rounded-sm border border-border object-contain" data-testid="license-preview" />
      )}
      <p className="text-xs leading-relaxed text-muted-foreground">
        Role rule: a verified <span className="font-bold text-foreground">GDL</span> license unlocks the Driver role.
        Standard licenses (B2, B, D, DA) restrict the account to Runner.
      </p>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFile} data-testid="license-file-input" />
    </div>
  );
}
